export const root = document.body.dataset.root || "/";
export const route = (path) =>
  `${root.endsWith("/") ? root : `${root}/`}${path.replace(/^\//, "")}`;
export const errorMessage = () => document.body.dataset.error;
export function readStore(key, fallback = []) {
  try {
    const value = JSON.parse(sessionStorage.getItem(key));
    if (value == null) return fallback;
    if (Array.isArray(fallback) && !Array.isArray(value)) return fallback;
    if (typeof fallback === "string" && typeof value !== "string") return fallback;
    return value;
  } catch {
    return fallback;
  }
}
export function safeStore(key, value) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {}
}
let toastTimer;
export function toast(message) {
  const dialog = [...document.querySelectorAll("dialog[open]")].at(-1);
  let el = dialog?.querySelector("[data-dialog-status]");
  if (dialog && !el) {
    el = document.createElement("p");
    el.dataset.dialogStatus = "";
    el.setAttribute("role", "status");
    dialog.append(el);
  }
  el ||= document.getElementById("toast");
  if (!el) return;
  clearTimeout(toastTimer);
  el.textContent = message;
  el.hidden = false;
  toastTimer = setTimeout(() => {
    el.hidden = true;
  }, 5000);
}
const dialogs = new WeakMap();
const canFocus = (el) =>
  el?.isConnected &&
  !el.closest("[hidden], [inert], dialog:not([open])") &&
  !el.disabled &&
  el.getClientRects().length;
export function dialogOrigin(dialog) {
  return dialogs.get(dialog)?.opener;
}
export function openDialog(dialog, opener = document.activeElement) {
  if (!dialog || dialog.open) return;
  const parent = opener?.closest("dialog");
  if (parent && parent !== dialog) opener = dialogOrigin(parent) || opener;
  if (!dialogs.has(dialog)) {
    dialog.addEventListener("close", () => {
      const state = dialogs.get(dialog);
      if (state?.opener?.getAttribute("aria-controls") === dialog.id)
        state.opener.setAttribute("aria-expanded", "false");
      if (
        dialog.open ||
        !state.restore ||
        document.querySelector("dialog[open]")
      )
        return;
      const target = canFocus(state.opener)
        ? state.opener
        : document.querySelector('[data-open="cart-drawer"]');
      if (canFocus(target)) target.focus({ preventScroll: true });
    });
  }
  document.querySelectorAll("dialog[open]").forEach((active) => {
    if (active !== dialog) closeDialog(active, false);
  });
  dialogs.set(dialog, { opener, restore: true });
  dialog.showModal();
  if (opener?.getAttribute("aria-controls") === dialog.id)
    opener.setAttribute("aria-expanded", "true");
}
export function closeDialog(dialog, restore = true) {
  if (!dialog?.open) return;
  const state = dialogs.get(dialog);
  if (state) state.restore = restore;
  dialog.close();
}
export function rendered(scope = document) {
  document.dispatchEvent(
    new CustomEvent("rs24:rendered", { detail: { scope } }),
  );
}
export function mount(selector, setup) {
  const instances = new WeakMap();
  const elements = (scope) => [
    ...(scope.matches?.(selector) ? [scope] : []),
    ...scope.querySelectorAll(selector),
  ];
  const init = (scope) =>
    elements(scope).forEach((el) => {
      if (!instances.has(el)) instances.set(el, setup(el) || (() => {}));
    });
  init(document);
  document.addEventListener("rs24:rendered", (event) =>
    init(event.detail.scope),
  );
  document.addEventListener("shopify:section:unload", (event) =>
    elements(event.target).forEach((el) => {
      instances.get(el)?.();
      instances.delete(el);
    }),
  );
}
export function cacheGet(key, age = 60000) {
  const entry = offerCache.get(`${presentationContext()}:${key}`);
  const elapsed = entry ? Date.now() - entry.time : Infinity;
  return elapsed >= 0 && elapsed < age ? entry.value : null;
}
export function cacheSet(key, value) {
  if (offerCache.size >= 40) offerCache.delete(offerCache.keys().next().value);
  offerCache.set(`${presentationContext()}:${key}`, { time: Date.now(), value });
}
const offerCache = new Map();
export const presentationContext = () => [document.documentElement.lang, document.body.dataset.currency,
  document.body.dataset.country, document.body.dataset.theme, document.body.dataset.preview, "3.1-rs1"].join(":");
export const compareIdentity = entry => `${entry.handle}:${entry.variant}`;
export function compareSelection() {
  const seen = new Set();
  return readStore("rs24-compare-v3").filter(entry => {
    if (!entry || typeof entry.handle !== "string" || !/^[a-z0-9-]{1,255}$/.test(entry.handle) ||
      typeof entry.variant !== "string" || !/^\d+$/.test(entry.variant) || typeof entry.group !== "string" || !entry.group.trim()) return false;
    const id = compareIdentity(entry);
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  }).slice(0, 4);
}
function syncCompare() {
  const selected = compareSelection();
  document.querySelectorAll("[data-compare-count]").forEach((el) => {
    el.textContent = selected.length;
  });
  document
    .querySelectorAll("[data-compare-add]")
    .forEach((el) =>
      el.setAttribute(
        "aria-pressed",
        String(selected.some((p) => p.handle === el.dataset.compareAdd && p.variant === el.dataset.compareVariant)),
      ),
    );
}
let quickModule, compareModule, displayModule;
export function languageDestination(language, country) {
  const alternatives = [
    ...document.querySelectorAll('link[rel="alternate"][hreflang]'),
  ];
  const codes = [
    `${language}-${country}`.toLowerCase(),
    language.toLowerCase(),
  ];
  for (const code of codes) {
    const alternate = alternatives.find(
      (item) => item.getAttribute("hreflang").toLowerCase() === code,
    );
    if (!alternate) continue;
    const url = new URL(alternate.href, location.origin);
    if (url.origin !== location.origin) continue;
    const currentParams = new URL(location.href).searchParams;
    for (const key of new Set(currentParams.keys())) {
      if (
        ["variant", "option_values", "q", "type", "sort_by", "page", "options[prefix]"].includes(
          key,
        ) ||
        key.startsWith("filter.")
      ) {
        url.searchParams.delete(key);
        for (const value of new Set(currentParams.getAll(key)))
          url.searchParams.append(key, value);
      }
    }
    return url.href;
  }
  return null;
}
document.addEventListener("click", async (event) => {
  const language = event.target.closest("[data-language-link]");
  if (language) {
    const target = languageDestination(
      language.hreflang,
      language.closest("[data-country]")?.dataset.country || "",
    );
    if (target) language.href = target;
    return;
  }
  const quick = event.target.closest("[data-quick-view]");
  if (quick) {
    event.preventDefault();
    if (quick.hasAttribute("aria-busy")) return;
    quick.setAttribute("aria-busy", "true");
    try {
      quickModule ||= import("@redstar/quick-view").catch((e) => {
        quickModule = null;
        throw e;
      });
      await (await quickModule).showQuickView(quick);
    } catch {
      toast(errorMessage());
    } finally {
      quick.removeAttribute("aria-busy");
    }
    return;
  }
  const compare = event.target.closest(
    '[data-compare-add], [data-open="compare-dialog"], [data-compare-remove], [data-compare-clear]',
  );
  if (compare) {
    event.preventDefault();
    try {
      compareModule ||= import("@redstar/compare").catch((e) => {
        compareModule = null;
        throw e;
      });
      (await compareModule).act(compare);
    } catch {
      toast(errorMessage());
    }
    return;
  }
  const display = event.target.closest("[data-display-preferences]");
  if (display) {
    event.preventDefault();
    if (display.hasAttribute("aria-busy")) return;
    display.setAttribute("aria-busy", "true");
    try {
      displayModule ||= import("@redstar/display").catch((error) => { displayModule = null; throw error; });
      (await displayModule).showPreferences(display);
    } catch { toast(errorMessage()); }
    finally { display.removeAttribute("aria-busy"); }
    return;
  }
  const opener = event.target.closest("[data-open]");
  if (opener) {
    const dialog = document.getElementById(opener.dataset.open);
    if (dialog) {
      event.preventDefault();
      openDialog(dialog, opener);
    }
  }
  if (event.target.closest("[data-close]"))
    closeDialog(event.target.closest("dialog"));
  const quantity = event.target.closest("[data-quantity]");
  if (quantity) {
    const input = quantity.parentElement.querySelector("input");
    if (quantity.dataset.quantity === "1") input.stepUp();
    else input.stepDown();
    input.dispatchEvent(new Event("change", { bubbles: true }));
  }
  document.querySelectorAll(".nav-item[open]").forEach((item) => {
    if (!item.contains(event.target)) item.open = false;
  });
  if (event.target instanceof HTMLDialogElement) {
    const r = event.target.getBoundingClientRect();
    if (
      event.clientX < r.left ||
      event.clientX > r.right ||
      event.clientY < r.top ||
      event.clientY > r.bottom
    )
      closeDialog(event.target);
  }
});
document.addEventListener("keydown", (event) => {
  if (event.key !== "Escape") return;
  const item = event.target.closest(".nav-item[open]");
  if (item) {
    item.open = false;
    item.querySelector("summary").focus();
    event.preventDefault();
  }
});
document.addEventListener(
  "toggle",
  (event) => {
    if (!event.target.matches?.(".nav-item[open]")) return;
    document.querySelectorAll(".nav-item[open]").forEach((item) => {
      if (item !== event.target) item.open = false;
    });
  },
  true,
);
document.addEventListener("submit", (event) => {
  if (document.body.dataset.preview !== "true") return;
  const type = event.target.querySelector("[name=form_type]")?.value;
  if (type === "contact" || type === "customer") {
    event.preventDefault();
    toast(document.body.dataset.previewForm);
  }
});
document.addEventListener("shopify:section:load", (event) =>
  rendered(event.target),
);
document.addEventListener("rs24:rendered", syncCompare);
syncCompare();
mount("[data-display-preferences]", button => { button.disabled = false; });

mount(".desktop-navigation", (nav) => {
  const triggers = () => [...nav.children].map((item) =>
    item.matches("details") ? item.querySelector("summary") : item,
  );
  const closeMenus = (except) => nav.querySelectorAll(".nav-item[open]").forEach((item) => {
    if (item !== except) item.open = false;
  });
  const onFocus = (event) => closeMenus(event.target.closest(".nav-item"));
  const onBlur = (event) => {
    if (!nav.contains(event.relatedTarget)) closeMenus();
  };
  const onKey = (event) => {
    const items = triggers();
    const index = items.indexOf(event.target);
    const menu = event.target.closest(".nav-item");
    if (index >= 0 && ["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) {
      event.preventDefault();
      const next = event.key === "Home" ? 0 : event.key === "End" ? items.length - 1 :
        (index + (event.key === "ArrowRight" ? 1 : -1) + items.length) % items.length;
      closeMenus();
      items[next].focus();
    } else if (index >= 0 && menu && ["ArrowDown", "ArrowUp"].includes(event.key)) {
      event.preventDefault();
      closeMenus(menu);
      menu.open = true;
      const links = [...menu.querySelectorAll(".mega-panel a")];
      (event.key === "ArrowUp" ? links.at(-1) : links[0])?.focus();
    }
  };
  nav.addEventListener("focusin", onFocus);
  nav.addEventListener("focusout", onBlur);
  nav.addEventListener("keydown", onKey);
  return () => {
    nav.removeEventListener("focusin", onFocus);
    nav.removeEventListener("focusout", onBlur);
    nav.removeEventListener("keydown", onKey);
  };
});

function loadComponents(scope) {
  const has = (selector) =>
    scope.matches?.(selector) || scope.querySelector(selector);
  if (has("[data-predictive]")) import("@redstar/search").catch(() => {});
  if (has("[data-product-section]"))
    import("@redstar/product").catch(() => {});
  if (has("[data-catalog]")) import("@redstar/filters").catch(() => {});
  if (has("[data-recommendations],[data-recently-viewed]"))
    import("@redstar/lazy").catch(() => {});
  if (has("[data-calculator]")) import("@redstar/calculator").catch(() => {});
}
loadComponents(document);
document.addEventListener("rs24:rendered", (event) =>
  loadComponents(event.detail.scope),
);
